import { useMemo } from 'react';
import type { OUItem } from '@/api/types';
import { buildOuTree, collectSubtreeIds, findOuNodePath, type OuNode } from './ouTree';

export type OuCheckState = 'checked' | 'indeterminate' | 'unchecked';

function annotateStates(
  nodes: OuNode[],
  selectedIds: ReadonlySet<string>,
  ancestorSelected: boolean,
  states: Map<string, OuCheckState>,
): void {
  for (const node of nodes) {
    const nodeSelected = ancestorSelected || selectedIds.has(node.ou.id);
    if (nodeSelected) {
      states.set(node.ou.id, 'checked');
      annotateStates(node.children, selectedIds, true, states);
      continue;
    }
    annotateStates(node.children, selectedIds, false, states);
    const hasSelectedChild = node.children.some(child => states.get(child.ou.id) !== 'unchecked');
    states.set(node.ou.id, hasSelectedChild ? 'indeterminate' : 'unchecked');
  }
}

/** Calcula el estado tri-state de cada nodo del árbol dado el conjunto colapsado `value`. */
export function computeOuCheckStates(tree: OuNode[], value: readonly string[]): Map<string, OuCheckState> {
  const states = new Map<string, OuCheckState>();
  annotateStates(tree, new Set(value), false, states);
  return states;
}

/**
 * Quita de `selectedIds` cualquier id cuyo ancestro también esté seleccionado: ese id es
 * redundante porque el ancestro ya implica todo su subárbol (formato colapsado). Necesario
 * porque `value` puede venir de datos externos/legados con ids inconsistentes, no solo de
 * este mismo hook.
 */
function normalizeSelectedIds(tree: OuNode[], selectedIds: Set<string>): void {
  for (const candidateId of [...selectedIds]) {
    const candidatePath = findOuNodePath(tree, candidateId);
    if (!candidatePath) continue;
    const hasSelectedAncestor = candidatePath
      .slice(0, -1)
      .some(ancestorNode => selectedIds.has(ancestorNode.ou.id));
    if (hasSelectedAncestor) selectedIds.delete(candidateId);
  }
}

/** Deriva el nuevo `value` (formato colapsado: el id de un nodo implica todo su subárbol) tras alternar `nodeId`. */
export function toggleOuSelection(tree: OuNode[], value: readonly string[], nodeId: string): string[] {
  const targetPath = findOuNodePath(tree, nodeId);
  if (!targetPath) return [...value];

  const states = computeOuCheckStates(tree, value);
  const selectedIds = new Set(value);
  normalizeSelectedIds(tree, selectedIds);

  if (states.get(nodeId) === 'checked') {
    if (selectedIds.has(nodeId)) {
      selectedIds.delete(nodeId);
      return [...selectedIds];
    }
    const ancestorIndex = targetPath.findIndex(ancestor => selectedIds.has(ancestor.ou.id));
    selectedIds.delete(targetPath[ancestorIndex]!.ou.id);
    for (let level = ancestorIndex; level < targetPath.length - 1; level++) {
      const keepId = targetPath[level + 1]!.ou.id;
      for (const sibling of targetPath[level]!.children) {
        if (sibling.ou.id !== keepId) selectedIds.add(sibling.ou.id);
      }
    }
    return [...selectedIds];
  }

  collectSubtreeIds(targetPath[targetPath.length - 1]!).forEach(
    subtreeNodeId => selectedIds.delete(subtreeNodeId),
  );
  selectedIds.add(nodeId);

  let currentPath = targetPath;
  while (currentPath.length > 1) {
    const parent = currentPath[currentPath.length - 2]!;
    const allChildrenSelected = parent.children.every(child => selectedIds.has(child.ou.id));
    if (!allChildrenSelected) break;
    parent.children.forEach(child => selectedIds.delete(child.ou.id));
    selectedIds.add(parent.ou.id);
    currentPath = currentPath.slice(0, -1);
  }

  return [...selectedIds];
}

/**
 * ¿La transición de `current` a `next` deja SIN CUBRIR algún id bloqueado que `current` sí cubría?
 *
 * 🔴 La comparación es por ESTADO CALCULADO, no por pertenencia a la lista de ids. Un id se puede
 * dejar de cubrir por tres caminos distintos —desmarcarlo, desmarcar un ancestro suyo que lo
 * cubría, o desmarcar todos sus descendientes— y una guarda escrita como `next.includes(id)`
 * solo tapa el primero. Recalcular el tri-state antes y después los cubre los tres a la vez.
 *
 * Solo bloquea la transición `cubierto → no cubierto`. Un id bloqueado que YA venía sin cubrir no
 * congela la interacción: pasa con una campaña creada por un admin global cuyo alcance no incluye
 * la OU del que la edita, y forzarlo a añadirse cambiaría el alcance de la campaña ajena.
 * Pasar de `checked` a `indeterminate` (afinar una sub-rama) tampoco se bloquea: la OU sigue
 * cubierta en parte, que es lo que exige RF-081 —«no puede deseleccionarlo»—, no que sea íntegra.
 */
export function violatesLockedIds(
  tree: OuNode[],
  current: readonly string[],
  next: readonly string[],
  lockedIds: readonly string[],
): boolean {
  if (lockedIds.length === 0) return false;
  const before = computeOuCheckStates(tree, current);
  const after = computeOuCheckStates(tree, next);
  // Un id que no está en el árbol no aparece en ninguno de los dos mapas y nunca bloquea nada.
  return lockedIds.some(id => before.get(id) !== 'unchecked' && after.get(id) === 'unchecked');
}

export interface UseOuSelectionResult {
  tree: OuNode[];
  getState: (nodeId: string) => OuCheckState;
  toggle: (nodeId: string) => void;
  /** ¿Este nodo se pinta bloqueado? Solo cuando está `checked`: es el único clic que lo apagaría. */
  isLocked: (nodeId: string) => boolean;
}

export function useOuSelection(
  items: OUItem[],
  value: readonly string[],
  onChange: (ids: string[]) => void,
  lockedIds: readonly string[] = [],
): UseOuSelectionResult {
  const tree = useMemo(() => buildOuTree(items), [items]);
  const states = useMemo(() => computeOuCheckStates(tree, value), [tree, value]);

  function getState(nodeId: string): OuCheckState {
    return states.get(nodeId) ?? 'unchecked';
  }

  function isLocked(nodeId: string): boolean {
    return lockedIds.includes(nodeId) && getState(nodeId) === 'checked';
  }

  function toggle(nodeId: string): void {
    const next = toggleOuSelection(tree, value, nodeId);
    // La casilla deshabilitada es la capa de UX; esta es la que de verdad garantiza la regla,
    // porque el desmarcado también llega desde el ancestro y desde los descendientes.
    if (violatesLockedIds(tree, value, next, lockedIds)) return;
    onChange(next);
  }

  return { tree, getState, toggle, isLocked };
}
